import { FC, ReactNode, useCallback, useState } from 'react'
import { AppraisalStepper } from '../../../../components/custom_components/Appraisal/AppraisalStepper/AppraisalStepper'
import { ContentContainer } from '../../../../components/custom_components/ContentContainer'
import { useCollapse } from '../../../../routes/collapse-context'
import { FullMeetingFragment, MeetingStep, MeetingStepStatus, MeetingStepType, MeetingStepViewId } from '../../../../types'
import { AppraisalFlowProvider } from '../shared/AppraisalFlowProvider'
import { setMeetingStep } from '../shared/AppraisalMeetingService'
import { AppraisalBackBtn, AppraisalNextBtn } from '../shared/MeetingStepButton'
import EmployeeMeetingStepEvaluation from './EmployeeMeetingStepEvaluation'
import EmployeeMeetingStepFeedback from './EmployeeMeetingStepFeedback'
import EmployeePreparationOverview from './EmployeePreparationOverview'
import EmployeePreparationSelfReflection from './EmployeePreparationSelfReflection'
import EmployeeMeetingStepDevelopment from './targets/EmployeeMeetingStepDevelopment.wrapper'

interface Props {
  meeting: FullMeetingFragment
}

const EmployeePrepareMeetingView: FC<Props> = ({ meeting }) => {
  const meetingId = meeting.id
  const [animate, setAnimate] = useState(false)
  const [processing, setProcessing] = useState(false)
  const collapsed = useCollapse()

  const steps: MeetingStep[] = meeting.meetingSteps
    ? meeting.meetingSteps.filter((step) => step.type === MeetingStepType.EmployeePrep).sort((a, b) => a.position - b.position)
    : []

  const activeStep = steps.find((x) => meeting.employeePreparationActiveStep && x.id === meeting.employeePreparationActiveStep.id)
  const activeStepIndex = activeStep ? steps.indexOf(activeStep) : -1

  //todo: create a more robust version
  const nextStep = steps[activeStepIndex + 1]
  const previousStep = steps[activeStepIndex - 1]

  const updateMeetingStep = useCallback(
    (step: MeetingStep, currentStepStatus?: MeetingStepStatus, callback?: () => void) => {
      setAnimate(true)
      setProcessing(true)
      if (callback) {
        callback()
      }
      setMeetingStep(meetingId, step, currentStepStatus).then(() => {
        setProcessing(false)
      })
    },
    [meetingId],
  )

  const BackBtn = () => {
    return <AppraisalBackBtn nextStep={previousStep} onClick={() => updateMeetingStep(previousStep)} />
  }
  const NextBtn = (currentStepStatus?: MeetingStepStatus, disabled?: boolean, tooltip?: string | ReactNode, callback?: () => void) => {
    return (
      <AppraisalNextBtn
        nextStep={nextStep}
        disabled={disabled}
        tooltip={tooltip}
        onClick={() => {
          updateMeetingStep(nextStep, currentStepStatus, callback)
        }}
      />
    )
  }

  return (
    <ContentContainer collapsed={collapsed}>
      <AppraisalFlowProvider meeting={meeting} steps={steps}>
        <AppraisalStepper
          meetingType={meeting.type}
          employee={meeting.employee}
          steps={steps}
          activeStepIndex={activeStepIndex}
          animate={animate}
          processing={processing}
          onStepClick={updateMeetingStep}
        />
        {activeStep && activeStep.viewId === MeetingStepViewId.Intro && <EmployeePreparationOverview meeting={meeting} nextBtn={NextBtn} />}
        {activeStep && activeStep.viewId === MeetingStepViewId.SelfReflection && (
          <EmployeePreparationSelfReflection meeting={meeting} backBtn={BackBtn} nextBtn={NextBtn} />
        )}
        {activeStep && activeStep.viewId === MeetingStepViewId.Evaluation && (
          <EmployeeMeetingStepEvaluation meeting={meeting} backBtn={BackBtn} nextBtn={NextBtn} />
        )}
        {activeStep && activeStep.viewId === MeetingStepViewId.DevelopmentAndTargets && (
          <EmployeeMeetingStepDevelopment backBtn={BackBtn} nextBtn={NextBtn} meeting={meeting} />
        )}
        {activeStep && activeStep.viewId === MeetingStepViewId.Feedback && (
          <EmployeeMeetingStepFeedback meeting={meeting} backBtn={BackBtn} />
        )}
      </AppraisalFlowProvider>
    </ContentContainer>
  )
}

export default EmployeePrepareMeetingView
